import { mutationAtom } from 'lib/atoms';
import request from 'lib/request';
import type { Response } from 'lib/request/types';
import type { WritableAtom } from 'jotai';

// ---------------------------------------------------------------------------
// Shared mutation factory for the document-structuring sections (AFE:
// Identification / Wells / Costs; DO: Overview / Interest / Wells). Each
// section's edit / confirm / revert / lifecycle action is a uniform
// { url, method, data } mutation atom. We use the low-level `mutationAtom`
// primitive (rather than `createMutationAtom`) to avoid registering
// structuring payload keys in the shared `api-payloads.ts` registry. The setter
// returns the request Response so callers can branch on success/error.
//
// Lives in `lib/atoms` (not a feature folder) so both the AFE and DO structuring
// atom layers depend on shared infrastructure rather than reaching across the
// feature boundary into one another.
// ---------------------------------------------------------------------------

// The two refusal envelopes: coded `{ errors: [{ code, message }] }` and a plain `{ error }`.
export interface WriteFailure {
  error?: string;
  errors?: ({ code?: string; message?: string } | null)[];
}

export const writeFailureMessage = (data?: WriteFailure): string | undefined =>
  (Array.isArray(data?.errors)
    ? data?.errors
        .map((entry) => entry?.message)
        .filter(Boolean)
        .join(' ')
    : undefined) || data?.error;

export interface MutationArg<Data> {
  url: string;
  method: 'POST' | 'PATCH' | 'DELETE';
  data?: unknown;
  onSuccess?: (data: Data) => void;
  onError?: (message?: string) => void;
}

// The atom shape returned by `buildSectionMutationAtom`. Exposed so callers can
// name the atom type without a `typeof buildSectionMutationAtom<T>` instantiation
// expression (which our prettier parser can't handle).
export type SectionMutationAtom<Data> = WritableAtom<
  Promise<Response<Data>> | null,
  [MutationArg<Data>],
  Promise<Response<Data>>
>;

export const buildSectionMutationAtom = <Data>() =>
  mutationAtom<Response<Data>, [MutationArg<Data>]>(
    async (_get, _set, arg): Promise<Response<Data>> => {
      const controller = new AbortController();
      const runner =
        arg.method === 'POST'
          ? request.post
          : arg.method === 'PATCH'
            ? request.patch
            : request.deleteReq;

      const response = await runner<Data, WriteFailure>(
        arg.url,
        arg.data ?? null,
        controller
      );

      if (response.type === 'success') {
        arg.onSuccess?.(response.data);
      } else if (response.type === 'error') {
        arg.onError?.(writeFailureMessage(response.data));
      } else if (response.type === 'fatal') {
        arg.onError?.(response.message);
      }
      return response;
    }
  );
